import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getMyPermissions } from '../api/permissions';
import { PagePermissions, EffectivePermissions } from '../types/permissions';

interface PermissionsContextType {
  permissions: Record<string, PagePermissions>;
  overrides: { role: string[]; user: string[] };
  loading: boolean;
  refresh: () => Promise<void>;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  permissions: {},
  overrides: { role: [], user: [] },
  loading: true,
  refresh: async () => {},
});

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const location = useLocation();
  const [permissions, setPermissions] = useState<Record<string, PagePermissions>>({});
  const [overrides, setOverrides] = useState<{ role: string[]; user: string[] }>({ role: [], user: [] });
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user || !token) {
      setPermissions({});
      setOverrides({ role: [], user: [] });
      setLoading(false);
      return;
    }
    try {
      const data: EffectivePermissions = await getMyPermissions();
      setPermissions(data.permissions);
      setOverrides(data.overrides);
    } catch (err) {
      console.error('[Permissions] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, location.pathname]);

  return (
    <PermissionsContext.Provider value={{ permissions, overrides, loading, refresh: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};
